import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { 
  MessageSquare, Send, Inbox, ArrowLeft, RefreshCcw, User, 
  Clock, Mail, Bell, ChevronRight, Megaphone, Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudentMessages() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('inbox');
  const [inboxMessages, setInboxMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Compose states
  const [showCompose, setShowCompose] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [sending, setSending] = useState(false);

  const token = localStorage.getItem('studentToken');

  useEffect(() => {
    if (!token) {
      navigate('/student/login');
      return;
    }
  }, [token, navigate]);

  const fetchInbox = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/messages/student/inbox`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInboxMessages(data);
      }
    } catch (error) {
      console.error('Error fetching inbox:', error);
    }
  }, [token]);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/messages/student/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  }, [token]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/messages/student/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [token]);

  const fetchTeachers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messages/student/teachers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTeachers(data);
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([fetchInbox(), fetchConversations(), fetchUnreadCount()]);
      setIsLoading(false);
    };
    loadData();

    // Poll for new messages every 30 seconds
    const interval = setInterval(() => {
      fetchUnreadCount();
      fetchInbox();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchInbox, fetchConversations, fetchUnreadCount]);

  const openConversation = async (conversationId) => {
    try {
      const res = await fetch(`${API_URL}/api/messages/student/conversation/${conversationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedConversation(data.conversation);
        setMessages(data.messages);
        fetchConversations();
        fetchUnreadCount();
      }
    } catch (error) {
      toast.error('Failed to load conversation');
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await fetch(`${API_URL}/api/messages/student/${messageId}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchInbox();
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const sendMessage = async () => {
    if (!selectedTeacher || !subject.trim() || !content.trim()) {
      toast.error('Please select a teacher and fill all fields');
      return;
    }

    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/messages/student/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient_id: selectedTeacher.id,
          recipient_type: 'staff',
          subject,
          content,
          message_type: 'direct'
        })
      });

      if (res.ok) {
        toast.success('Message sent successfully!');
        setShowCompose(false);
        resetCompose();
        fetchConversations();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const sendReply = async () => {
    if (!replyContent.trim() || !selectedConversation) return;

    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/messages/student/reply/${selectedConversation.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: replyContent })
      });

      if (res.ok) {
        setReplyContent('');
        openConversation(selectedConversation.id);
        toast.success('Reply sent');
      } else {
        toast.error('Failed to send reply');
      }
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const resetCompose = () => {
    setSelectedTeacher(null);
    setSubject('');
    setContent('');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  const getMessageIcon = (type) => {
    if (type === 'broadcast') return <Megaphone className="text-yellow-500" size={16} />;
    if (type === 'group') return <Users className="text-blue-500" size={16} />;
    return <User className="text-indigo-500" size={16} />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCcw className="animate-spin h-8 w-8 text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/student/dashboard')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
              <MessageSquare className="text-indigo-600" />
              Messages
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
              <Bell size={12} />
              {unreadCount}
            </span>
          )}
          <Button 
            size="sm"
            onClick={() => { setShowCompose(true); fetchTeachers(); }} 
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            <Send size={14} className="mr-1" />
            New
          </Button>
        </div>
      </div>

      {/* Compose Modal */}
      {showCompose && (
        <Card className="border-2 border-indigo-200">
          <CardHeader className="bg-indigo-50 py-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Send className="text-indigo-600" size={18} />
                New Message
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => { setShowCompose(false); resetCompose(); }}>
                ✕
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            {/* Teacher Selection */}
            <div>
              <label className="text-sm font-medium text-gray-700">Send to Teacher/Staff</label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded-lg p-2">
                {teachers.map(teacher => (
                  <div
                    key={teacher.id}
                    onClick={() => setSelectedTeacher(teacher)}
                    className={`p-2 border rounded cursor-pointer flex items-center gap-2 ${selectedTeacher?.id === teacher.id ? 'border-indigo-500 bg-indigo-50' : 'hover:border-gray-400'}`}
                  >
                    <User size={16} className="text-gray-400" />
                    <div>
                      <p className="font-medium text-sm">{teacher.name}</p>
                      <p className="text-xs text-gray-500">{teacher.designation || 'Staff'}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Subject & Content */}
            <div>
              <label className="text-sm font-medium text-gray-700">Subject</label>
              <Input
                placeholder="What is this about?"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Message</label>
              <textarea
                placeholder="Write your message..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={3}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => { setShowCompose(false); resetCompose(); }}>
                Cancel
              </Button>
              <Button 
                size="sm"
                onClick={sendMessage} 
                disabled={sending || !selectedTeacher || !subject || !content}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {sending ? <RefreshCcw className="animate-spin mr-1" size={14} /> : <Send size={14} className="mr-1" />}
                Send
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('inbox')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'inbox' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500'}`}
        >
          <Inbox size={16} className="inline mr-1" />
          Inbox ({inboxMessages.filter(m => !m.is_read).length})
        </button>
        <button
          onClick={() => setActiveTab('conversations')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'conversations' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500'}`}
        >
          <MessageSquare size={16} className="inline mr-1" />
          Conversations
        </button>
      </div>

      {/* Content */}
      {activeTab === 'inbox' && !selectedConversation && (
        <div className="space-y-2">
          {inboxMessages.length === 0 ? (
            <Card className="p-8 text-center">
              <Mail className="mx-auto h-12 w-12 text-gray-300 mb-2" />
              <p className="text-gray-500">No messages yet</p>
            </Card>
          ) : (
            inboxMessages.map(msg => (
              <Card 
                key={msg.id} 
                className={`cursor-pointer hover:shadow-md transition-shadow ${!msg.is_read ? 'border-l-4 border-indigo-500 bg-indigo-50/50' : ''}`}
                onClick={() => {
                  if (msg.message_type === 'direct' && msg.conversation_id) {
                    openConversation(msg.conversation_id);
                  } else {
                    markAsRead(msg.id);
                  }
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-1">
                      {getMessageIcon(msg.message_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className={`font-medium text-sm ${!msg.is_read ? 'text-gray-900' : 'text-gray-600'}`}>
                          {msg.sender_name}
                        </p>
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <Clock size={12} />
                          {formatDate(msg.created_at)}
                        </span>
                      </div>
                      <p className={`text-sm ${!msg.is_read ? 'font-medium text-gray-800' : 'text-gray-600'}`}>
                        {msg.subject}
                      </p>
                      <p className="text-sm text-gray-500 truncate">{msg.content}</p>
                      {msg.message_type !== 'direct' && (
                        <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded ${msg.message_type === 'broadcast' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                          {msg.message_type === 'broadcast' ? 'Broadcast' : 'Course Announcement'}
                        </span>
                      )}
                    </div>
                    <ChevronRight size={16} className="text-gray-400 mt-2" />
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {activeTab === 'conversations' && !selectedConversation && (
        <div className="space-y-2">
          {conversations.length === 0 ? (
            <Card className="p-8 text-center">
              <MessageSquare className="mx-auto h-12 w-12 text-gray-300 mb-2" />
              <p className="text-gray-500">No conversations yet</p>
              <p className="text-sm text-gray-400">Start a conversation by sending a message to a teacher</p>
            </Card>
          ) : (
            conversations.map(conv => (
              <Card 
                key={conv.id}
                onClick={() => openConversation(conv.id)}
                className={`cursor-pointer hover:shadow-md transition-shadow ${conv.unread_count > 0 ? 'border-l-4 border-indigo-500' : ''}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <User className="text-indigo-600" size={20} />
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {conv.participant_1_type === 'student' ? conv.participant_2_name : conv.participant_1_name}
                        </p>
                        <p className="text-sm text-gray-500 truncate max-w-[200px]">{conv.last_message}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {conv.unread_count > 0 && (
                        <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                          {conv.unread_count}
                        </span>
                      )}
                      <p className="text-xs text-gray-400 mt-1">{formatDate(conv.last_message_at)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Conversation View */}
      {selectedConversation && (
        <Card>
          <CardHeader className="border-b bg-gray-50 py-3">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => setSelectedConversation(null)}>
                <ArrowLeft size={16} />
              </Button>
              <div>
                <CardTitle className="text-base">
                  {selectedConversation.participant_1_type === 'student' 
                    ? selectedConversation.participant_2_name 
                    : selectedConversation.participant_1_name}
                </CardTitle>
                <CardDescription className="text-xs">
                  {selectedConversation.participant_1_type === 'student' 
                    ? selectedConversation.participant_2_type 
                    : selectedConversation.participant_1_type}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* Messages */}
            <div className="h-[300px] overflow-y-auto p-4 space-y-3">
              {messages.map(msg => {
                const isMe = msg.sender_type === 'student';
                return (
                  <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-lg p-3 ${isMe ? 'bg-indigo-600 text-white' : 'bg-gray-100'}`}>
                      <p className={`text-xs mb-1 ${isMe ? 'text-indigo-200' : 'text-gray-500'}`}>
                        {msg.sender_name} • {formatDate(msg.created_at)}
                      </p>
                      {msg.subject && msg.subject !== 'Re: Conversation' && (
                        <p className={`font-medium text-sm mb-1 ${isMe ? 'text-white' : 'text-gray-800'}`}>
                          {msg.subject}
                        </p>
                      )}
                      <p className={`text-sm ${isMe ? 'text-white' : 'text-gray-700'}`}>{msg.content}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Reply Input */}
            <div className="border-t p-3 flex gap-2">
              <Input
                placeholder="Type your reply..."
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendReply()}
                className="flex-1 text-sm"
              />
              <Button 
                size="sm"
                onClick={sendReply} 
                disabled={sending || !replyContent.trim()}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {sending ? <RefreshCcw className="animate-spin" size={14} /> : <Send size={14} />}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
